import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Container, CircularProgress, Alert, Button, useTheme, useMediaQuery } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { IntakeForm } from './IntakeForm';
import { IntakeService } from '../services/intakeService';
import { PetProfile } from '../types/onboarding';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import LanguageSwitcher from './LanguageSwitcher';

const IntakeFormRoute: React.FC = () => {
    const { professionalId } = useParams<{ professionalId: string }>();
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [professionalName, setProfessionalName] = useState<string>('PetSnap Professional');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchProfessional = async () => {
            if (!professionalId) {
                setError(t('intakeForm.route.invalidLink'));
                setLoading(false);
                return;
            }
            try {
                const docRef = doc(db, 'users', professionalId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    if (data.displayName) {
                        setProfessionalName(`${data.displayName} 's Pet Boarding & Care`);
                    }
                }
            } catch (err) {
                console.error('Failed to fetch professional:', err);
                // Continue even if name fails, just use fallback
            } finally {
                setLoading(false);
            }
        };
        fetchProfessional();
    }, [professionalId, t]);

    const handleSubmit = async (data: Partial<PetProfile>) => {
        if (!professionalId) return;

        setSubmitting(true);
        setError(null);
        try {
            await IntakeService.submitIntakeForm(professionalId, data);
            setSuccess(true);
        } catch (err: any) {
            console.error(err);
            setError(err.message || t('intakeForm.route.submitFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
                <CircularProgress />
                <Typography mt={2}>{t('intakeForm.route.loading')}</Typography>
            </Container>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, bgcolor: 'primary.main', color: 'white' }}>
                <LanguageSwitcher />
            </Box>

            {success ? (
                <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
                    <Alert severity="success" sx={{ mb: 4, textAlign: 'left' }}>
                        {t('intakeForm.route.success')}
                    </Alert>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth={isMobile}
                        startIcon={<HomeIcon />}
                        href="https://petsnap.datax.app"
                        sx={{
                            borderRadius: '50px',
                            px: 4,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)',
                            '&:hover': {
                                boxShadow: '0 6px 20px rgba(0,118,255,0.23)',
                            }
                        }}
                    >
                        {t('intakeForm.route.backToHome')}
                    </Button>
                </Container>
            ) : (
                <Container maxWidth="md" sx={{ py: 4, flex: 1 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 4 }}>
                            {error}
                        </Alert>
                    )}
                    <IntakeForm
                        professionalName={professionalName}
                        onSubmit={handleSubmit}
                        isSubmitting={submitting}
                    />
                </Container>
            )}
        </Box>
    );
};

export default IntakeFormRoute;
